/**
 * Luật thuần của màn chọn quyền chia đôi (`components/shared/ChonQuyenChiaDoi`): lọc, so với bản đã lưu, chọn hàng loạt.
 * Tách khỏi component để kiểm bằng `node --test`.
 */

export interface QuyenChon {
  code: string;
  /** Mô tả người dùng đọc — dữ liệu của server, không dịch. */
  nhan: string;
}

export interface NhomChon {
  khoa: string;
  nhan: string;
  quyen: QuyenChon[];
}

export type BoLocChon = 'tat-ca' | 'da-chon' | 'chua-chon';

/** So khớp không dấu, không phân biệt hoa thường: gõ `ke toan` vẫn ra `Kế toán`. */
export function boDau(s: string): string {
  return s
    .normalize('NFD')
    .replace(/\p{Diacritic}/gu, '')
    .replace(/đ/g, 'd')
    .replace(/Đ/g, 'D')
    .toLowerCase();
}

/**
 * Lọc nhóm theo từ khoá rồi theo trạng thái chọn; nhóm hết quyền thì bỏ.
 * Tên nhóm khớp từ khoá thì giữ mọi quyền của nhóm (như `LuoiChonQuyen`), bộ lọc trạng thái vẫn áp sau đó.
 */
export function locNhom(
  nhom: readonly NhomChon[],
  tuKhoa: string,
  boLoc: BoLocChon,
  daChon: ReadonlySet<string>,
): NhomChon[] {
  const k = boDau(tuKhoa.trim());
  return nhom
    .map((n) => {
      let quyen = n.quyen;
      if (k && !boDau(n.nhan).includes(k)) {
        quyen = quyen.filter((q) => boDau(q.nhan).includes(k) || q.code.toLowerCase().includes(k));
      }
      if (boLoc === 'da-chon') quyen = quyen.filter((q) => daChon.has(q.code));
      if (boLoc === 'chua-chon') quyen = quyen.filter((q) => !daChon.has(q.code));
      return quyen === n.quyen ? n : { ...n, quyen };
    })
    .filter((n) => n.quyen.length > 0);
}

/** Quyền thêm và bỏ so với bản đã lưu. */
export function soSanhQuyen(daLuu: ReadonlySet<string>, daChon: ReadonlySet<string>): { them: number; bo: number } {
  let them = 0;
  for (const c of daChon) if (!daLuu.has(c)) them++;
  let bo = 0;
  for (const c of daLuu) if (!daChon.has(c)) bo++;
  return { them, bo };
}

/** Khoá các nhóm có quyền khác bản đã lưu (để chấm báo "đã đổi"). Chỉ xét quyền thuộc danh mục. */
export function nhomDaDoi(
  nhom: readonly NhomChon[],
  daLuu: ReadonlySet<string>,
  daChon: ReadonlySet<string>,
): Set<string> {
  const ra = new Set<string>();
  for (const n of nhom) if (n.quyen.some((q) => daLuu.has(q.code) !== daChon.has(q.code))) ra.add(n.khoa);
  return ra;
}

/** Số quyền đang chọn trong nhóm. */
export function demChon(quyen: readonly QuyenChon[], daChon: ReadonlySet<string>): number {
  let n = 0;
  for (const q of quyen) if (daChon.has(q.code)) n++;
  return n;
}

/**
 * Chọn hoặc bỏ hàng loạt đúng các quyền đưa vào (đang hiện sau lọc): còn quyền nào chưa chọn thì chọn hết, không thì bỏ hết.
 * Không đụng quyền bị lọc ẩn.
 */
export function doiHangLoat(daChon: ReadonlySet<string>, quyen: readonly QuyenChon[]): Set<string> {
  const moi = new Set(daChon);
  const chonHet = quyen.some((q) => !daChon.has(q.code));
  for (const q of quyen) {
    if (chonHet) moi.add(q.code);
    else moi.delete(q.code);
  }
  return moi;
}

/**
 * Tách mô tả dạng `Việc chính: chi tiết…` thành tiêu đề và dòng phụ, để dòng quyền quét nhanh hơn.
 * Chỉ tách khi phần trước ngắn (≤ 60 ký tự) và còn phần sau; không thì giữ nguyên một dòng. Không đổi chữ nào.
 */
export function tachNhan(nhan: string): { chinh: string; phu?: string } {
  const i = nhan.indexOf(': ');
  if (i <= 0 || i > 60) return { chinh: nhan };
  const phu = nhan.slice(i + 2).trim();
  return phu ? { chinh: nhan.slice(0, i), phu } : { chinh: nhan };
}
